import { CONFIG } from "~/config";
import { _ } from "~/i18n";
import Scene from "~/scene";
import { frpc } from "~/util/general";
import { IFRPCResponse, IPoi, IWhereAmI } from "~/interfaces";
import { store } from "~/state/store";

export interface IPoiaggDetailResponse extends IFRPCResponse {
	poi: IPoi;
}

export interface IDescriptionData {
	[key: string]: Array<number>;
}

export interface IPOIIcon {
	height: number;
	id: number;
	width: number;
}

export interface IPOITypes {
	[key: number]: IPOIIcon;
}

export interface ILookupOpts {
	zoom: number;
	mapsetId: number;
	pixelSize: number;
	lang: Array<string>;
	approval?: boolean;
	sources?: Array<string>;
	zoomCorrection?: number | (() => number);
	count?: number;
	poiDistinct?: boolean;
}

export interface IDescriptionResult {
	[key: string]: IPoi;
}

export interface IDescriptionOpts {
	query?: string;
	fetchPhoto?: boolean;
	ratios?: Array<string>;
	lang?: Array<string>;
	reviews?: boolean;
	wikimedia?: boolean;
}

export interface IDescriptionOutput {
	status: number;
	poi: Array<IPoi>;
	result: IDescriptionResult;
	statusMessage: string;
	totalCount: number;
}

export const POIAGG_URL = "/poiagg";

const POITYPES: IPOITypes = {};

export function detail(source: string, id: string | number, options: any = {}): ReturnType<typeof frpc<IPoiaggDetailResponse>> {
	if (!source || !id) {
		return {
			request: null,
			promise: Promise.reject({
				status: 500,
			}),
		};
	} else if (source === "coor" || source === "closure") {
		const arr = id.toString().split(",");
		const coords = Scene.coordsFromWGS84(arr[0], arr[1]);
		const data = {
			status: 200,
			poi: {
				mark: {
					lon: parseFloat(arr[0]),
					lat: parseFloat(arr[1]),
				},
				title: _("detail.titleCoor"),
				description: Mapy.getComponent("gps").coordsToFormat(coords),
				source: source,
				id,
				inactive: false,
				media: {},
				extend: {},
				typeName: "",
				typeId: -1,
			},
		};

		if (source === "closure") {
			delete data.poi.title;
		}

		return {
			request: null,
			promise: Promise.resolve(data),
		};
	}

	// pokud je zapnuta redakce, posila se parametr, kterej poiagg nezna, aby se brala ziva data a obchazela cache
	// viz: https://gitlab.seznam.net/mapycz/mapy-poiagg/-/blob/master/src/method_lookup_helpers.cc#L99
	if (Mapy.CUS.editorial?.interface) {
		options.noCache = true;
		options.redakce = true;
	}

	// pro
	options.wikimedia = true;

	return frpc<IPoiaggDetailResponse>({
		url: CONFIG.requestUrl + POIAGG_URL,
		method: "detail",
		args: [source, id, options],
	});
}

export function poisToDescriptionOptions(pois: Array<Partial<IPoi>>): IDescriptionData {
	const options: IDescriptionData = {};

	pois.forEach(poi => {
		if (!poi.source || !poi.id) {
			return;
		}

		if (!(poi.source in options)) {
			options[poi.source] = [];
		}

		options[poi.source].push(poi.id as number);
	});

	Object.keys(options).forEach(source => {
		options[source] = [...Array.from(new Set(options[source]))];
	});

	return options;
}

function updateDescriptionData(descData: IDescriptionOutput, coorItems: Array<Partial<IPoi>>) {
	const result: IDescriptionResult = {};

	if (!Array.isArray(descData.poi)) {
		descData.poi = [];
	}

	// upravime normalni data
	descData.poi.forEach(poi => {
		// souradnice
		if (poi.mark) {
			// @ts-ignore
			poi.x = poi.mark.lon;
			// @ts-ignore
			poi.y = poi.mark.lat;
		}

		// adresa
		if (poi.titleVars) {
			// @ts-ignore
			poi.address = poi.titleVars.locationMain3 || poi.titleVars.locationMain2 || [poi.titleVars.locationMain1] || [];
		} else {
			// @ts-ignore
			poi.address = [];
		}

		// ulozime
		result[`${poi.source}${poi.id}`] = poi;
	});

	// pridame coor
	coorItems.forEach(coorItem => {
		const arr = typeof coorItem.id === "string"
			? coorItem.id.split(",")
			: ["0", "0"];
		const coords = Scene.coordsFromWGS84(arr[0], arr[1]);
		// @ts-ignore
		const coorData: IPoi = {
			mark: {
				lon: parseFloat(arr[0]),
				lat: parseFloat(arr[1]),
			},
			title: coords.toWGS84(3).reverse().join(" "),
			description: "",
			source: coorItem.source,
			id: coorItem.id,
		};

		// pridame do result + poi pole
		result[`coor${coorItem.id}`] = coorData;
		descData.poi.push(coorData);
	});

	descData.result = result;

	return descData;
}

export function description(pois: Array<Partial<IPoi>>, options: IDescriptionOpts = {}) {
	// prazdne volani
	if (pois.length === 0) {
		return Promise.resolve({
			poi: [],
			status: 200,
			statusMessage: "OK",
			totalCount: 0,
		}) as Promise<IDescriptionOutput>;
	}

	const coorItems: Array<Partial<IPoi>> = [];
	const items: Array<Partial<IPoi>> = [];

	pois.forEach(poi => {
		if (poi.source === "coor") {
			coorItems.push(poi);
		} else {
			items.push(poi);
		}
	});

	const data = poisToDescriptionOptions(items);

	return new Promise((resolve, reject) => {
		frpc({
			url: CONFIG.requestUrl + POIAGG_URL,
			method: "description",
			args: [data, options],
		}).promise.then((descData: IDescriptionOutput) => {
			const updatedData = updateDescriptionData(descData, coorItems);

			resolve(updatedData);
		}, error => reject(error));
	}) as Promise<IDescriptionOutput>;
}

export function buildLookupOptions(mapsetId: number, opts: Partial<ILookupOpts>): ILookupOpts {
	const mState = store.getState().map;
	const pixelSize = Scene.getPixelSize();
	const options = {
		zoom: 0,
		mapsetId,
		pixelSize: pixelSize,
		lang: CONFIG.langPriority,
		...opts,
	};
	const zoomCorrection = typeof options.zoomCorrection === "number"
		? options.zoomCorrection
		: typeof options.zoomCorrection === "function"
			? options.zoomCorrection()
			: 0;

	// nastavime zoom
	options.zoom = mState.zoom + zoomCorrection;

	if ("zoomCorrection" in options) {
		delete options.zoomCorrection;
	}

	return options;
}

export function lookupbox(lbx = 0, lby = 0, rtx = 0, rty = 0, opts: ILookupOpts) {
	const paramsObj = {
		zoom: 0,
		mapsetId: 0,
		pixelSize: 1,
		...Mapy.CUS.editorial?.interface
			? {
				noCache: true,
			}
			: {},
		...opts,
	};
	const params = [
		lbx,
		lby,
		rtx,
		rty,
		paramsObj,
	];
	const hints = {
		"0": "float",
		"1": "float",
		"2": "float",
		"3": "float",
		"4.pixelSize": "float",
	};

	return frpc({
		url: CONFIG.requestUrl + POIAGG_URL,
		method: "lookupbox",
		args: params,
		hints,
	});
}

export function lookupboxCadastral() {
	const viewport = Scene.getViewport();
	const { zoom } = store.getState().map;
	const cadastralAreas = true;
	const args = [{
		bbox: [viewport.lbx, viewport.lby, viewport.rtx, viewport.rty],
		zoom,
		cadastralAreas,
		lang: [CONFIG.defaultLang],
	}];
	const hints = {
		"0.bbox.0": "float",
		"0.bbox.1": "float",
		"0.bbox.2": "float",
		"0.bbox.3": "float",
	};

	return frpc({
		url: CONFIG.requestUrl + POIAGG_URL,
		method: "listCadastralEntity",
		args,
		hints,
	});
}

export async function initPOIIcons(): Promise<void> {
	/* eslint-disable no-console */
	// zpracujeme data, co prisly z BE
	if (CONFIG.poiicons) {
		const variants = Object.keys(CONFIG.poiicons);

		for (const variant of variants) {
			const sizes = variant.split("x").map(item => parseFloat(item));

			Object.keys(CONFIG.poiicons[variant]).forEach(item => {
				POITYPES[item] = {
					width: sizes[0],
					height: sizes[1],
					id: CONFIG.poiicons[variant][item],
				} as IPOIIcon;
			});
		}
	} else {
		try {
			console.log(`Poi ikony - neprisly z mapload -> nacteme lokalni`);

			const importData = await import(
				/* webpackChunkName: "poiicons" */
				"~/util/providers/poiaggData/poiicons"
			);
			const cacheResponse = importData.POIAGG_POIICONS_RESPONSE;

			cacheResponse.poitype.forEach(poiType => {
				const icon = cacheResponse.poiicon.find(poiIcon => poiIcon.id === poiType.icon_id);

				if (icon) {
					POITYPES[poiType.id] = icon;
				}
			});
		} catch (exc) {
			console.log(`Poi ikony - vyjimka behem nacteni ikonek z kese`);
		}
	}

	Object.freeze(POITYPES);
}

export function getIcon(type: number, options: { universal?: boolean; scale?: number; } = {}) {
	const iconId = type in POITYPES
		? POITYPES[type].id
		: null;

	if (!iconId && options.universal) {
		return "/img/universal-icon.png";
	}

	if (!iconId) {
		return "";
	}

	let iconSrc = iconId.toString();

	// scale rozmeru
	if (iconSrc && options.scale) {
		iconSrc += `?scale=${options.scale}`;
	}

	return `https://api.mapy.${CONFIG.TLD}/poiimg/icon/${iconSrc}`;
}

interface IWhereAmIResponse extends IFRPCResponse, IWhereAmI {}

export function whereami(lon: number, lat: number, zoom: number) {
	const hints = {
		0: "float",
		1: "float",
	};

	return frpc<IWhereAmIResponse>({
		url: CONFIG.requestUrl + POIAGG_URL,
		method: "whereami",
		args: [lon, lat, zoom, {}],
		hints,
	}).promise;
}

export async function whereamis(positions: Array<{
	longitude: number;
	latitude: number;
	zoom: number;
}>): Promise<Array<IWhereAmI["poi"]>> {
	const results = await Promise.all(positions.map(async position => {
		try {
			const hints = {
				0: "float",
				1: "float",
			};
			const response = await frpc<IWhereAmIResponse>({
				url: CONFIG.requestUrl + POIAGG_URL,
				method: "whereami",
				args: [position.longitude, position.latitude, position.zoom, {}],
				hints,
			}).promise;

			if (response.status === 200) {
				return response.poi;
			}

			throw new Error();
		} catch (ex) {
			// pokud to selze, tak vratime pro tuhle ten default se source coor
			return {
				source: "coor",
				id: `${position.longitude},${position.latitude}`,
				mark: {
					lon: position.longitude,
					lat: position.latitude,
				},
				title: `${position.longitude}, ${position.latitude}`,
				description: "",
			};
		}
	}));

	return results;
}
